import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

import { Checkbox } from '../../../components/ui/Checkbox';
import CaseTableRow from './CaseTableRow';

const CaseTable = ({
  cases,
  selectedCases,
  onSelectAll,
  onSelectCase,
  isAdmin = false,
  onAssignProsthetist,
  onAssignProductionStaff,
  /** dentiste_client : sans colonnes Client ni Personnel assigné */
  dentistClientView = false,
  viewerRole = null,
}) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ field: 'lastActivity', direction: 'desc' });

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sortConfig?.field !== field) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const sortedCases = [...(cases || [])].sort((a, b) => {
    let aValue = a?.[sortConfig?.field];
    let bValue = b?.[sortConfig?.field];

    if (dentistClientView && sortConfig?.field === 'phase') {
      aValue = a?.workflowProgress?.percent ?? 0;
      bValue = b?.workflowProgress?.percent ?? 0;
    }

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    if (sortConfig?.direction === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    }
    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
  });

  const allSelected = cases?.length > 0 && selectedCases?.length === cases?.length;
  const someSelected = selectedCases?.length > 0 && selectedCases?.length < cases?.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('patientName')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Patient
                  <Icon name={getSortIcon('patientName')} size={16} />
                </button>
              </th>
              {!dentistClientView && (
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('dentistName')}
                    className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                  >
                    Client
                    <Icon name={getSortIcon('dentistName')} size={16} />
                  </button>
                </th>
              )}
              <th className="px-4 py-3 text-left min-w-[8rem]">
                <button
                  onClick={() => handleSort('packageLabel')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Pack
                  <Icon name={getSortIcon('packageLabel')} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-foreground">Prothésiste </span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('phase')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  {dentistClientView ? t('caseWorkflowProgress.listColumn') : 'Phase'}
                  <Icon name={getSortIcon('phase')} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('urgency')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Urgence
                  <Icon name={getSortIcon('urgency')} size={16} />
                </button>
              </th>
              {!dentistClientView && (
                <th className="px-4 py-3 text-left">
                  <span className="text-sm font-semibold text-foreground">Personnel assigné</span>
                </th>
              )}
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('lastActivity')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Date
                  <Icon name={getSortIcon('lastActivity')} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 text-right whitespace-nowrap">
                <span className="text-sm font-semibold text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCases?.map((caseData) => (
              <CaseTableRow
                key={caseData?.id}
                caseData={caseData}
                isSelected={selectedCases?.includes(caseData?.id)}
                onSelect={onSelectCase}
                isAdmin={isAdmin}
                onAssignProsthetist={onAssignProsthetist}
                onAssignProductionStaff={onAssignProductionStaff}
                dentistClientView={dentistClientView}
                viewerRole={viewerRole}
              />
            ))}
          </tbody>
        </table>
      </div>
      {cases?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="FileX" size={48} className="text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Aucune conception trouvée</h3>
          <p className="text-sm text-muted-foreground">Essayez d'ajuster vos filtres ou créez une nouvelle conception</p>
        </div>
      )}
    </div>
  );
};

export default CaseTable;